import React, { useState } from 'react';
import { Check, Star, ArrowLeft, CreditCard, X, Globe, Sparkles, Clock, Shield, CheckCircle2, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface PlanSelectionScreenProps {
  onSelectPlan: (plan: string) => void;
  onBack?: () => void;
}
export function PlanSelectionScreen({
  onSelectPlan,
  onBack
}: PlanSelectionScreenProps) {
  const {
    isLight
  } = useTheme();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAdded, setPaymentAdded] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const freeMinutes = paymentAdded ? 30 : 15;
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) {
      return digits.slice(0, 2) + '/' + digits.slice(2);
    }
    return digits;
  };
  const isCardValid = cardNumber.replace(/\s/g, '').length >= 15 && expiry.length >= 4 && cvc.length >= 3;
  const handleAddPayment = () => {
    if (!isCardValid) return;
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setPaymentAdded(true);
      setShowPaymentModal(false);
      setCardNumber('');
      setExpiry('');
      setCvc('');
    }, 1500);
  };
  return <div className="h-full flex flex-col p-6 pb-24 relative overflow-y-auto" data-id="element-1412">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-1413" />
      <div className={`absolute bottom-40 -left-16 w-40 h-40 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-blue-500/10' : 'bg-blue-500/5'}`} data-id="element-1414" />

      {/* Back button */}
      {onBack && <div className="flex items-center mb-4 relative z-10" data-id="element-1415">
          <button onClick={onBack} className={`p-2 -ml-2 rounded-full border transition-colors ${isLight ? 'text-slate-500 hover:text-slate-900 bg-white/60 backdrop-blur-md border-slate-200' : 'text-zinc-400 hover:text-white bg-white/5 backdrop-blur-md border-white/10'}`} data-id="element-1416">
            <ArrowLeft className="w-5 h-5" data-id="element-1417" />
          </button>
        </div>}

      <div className="text-center mb-8 mt-4 relative z-10" data-id="element-1418">
        <h1 className={`text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1419">
          Choose Your Plan
        </h1>
        <p className={isLight ? 'text-slate-500' : 'text-zinc-400'} data-id="element-1420">
          Start free, upgrade anytime
        </p>
      </div>

      <div className="space-y-4 pb-8 relative z-10" data-id="element-1421">
        {/* Free Trial Card */}
        <div className={`relative rounded-2xl p-6 transition-all duration-500 ${isLight ? 'bg-white/60 backdrop-blur-xl border border-slate-200/50 shadow-sm' : 'bg-dark-card/40 backdrop-blur-xl border border-white/10 shadow-lg'}`} data-id="element-1422">
          <div className="flex justify-between items-start mb-4" data-id="element-1423">
            <div data-id="element-1424">
              <h3 className={`font-bold text-lg ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1425">
                Free Trial
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-1426">
                <span className={`text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1427">
                  $0
                </span>
              </div>
            </div>
            <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-400'}`} data-id="element-1428">
              7 days
            </div>
          </div>

          <ul className="space-y-3 mb-4" data-id="element-1429">
            {[{
            icon: Clock,
            text: `${freeMinutes} minutes of AI call handling`,
            highlight: paymentAdded
          }, {
            icon: Shield,
            text: 'Basic default AI settings'
          }, {
            icon: X,
            text: 'Cannot modify AI instructions',
            muted: true
          }, {
            icon: Globe,
            text: 'English language only'
          }].map((item, i) => <li key={i} className={`flex items-start gap-2.5 text-sm ${item.muted ? isLight ? 'text-slate-400' : 'text-zinc-500' : (item as any).highlight ? isLight ? 'text-vexu-700 font-medium' : 'text-vexu font-medium' : isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1430">
                <item.icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${item.muted ? isLight ? 'text-slate-300' : 'text-zinc-600' : (item as any).highlight ? isLight ? 'text-vexu-600' : 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1431" />
                {item.text}
              </li>)}
          </ul>

          {/* Payment method upsell / success */}
          {paymentAdded ? <div className={`rounded-xl p-3.5 mb-5 flex items-center gap-3 ${isLight ? 'bg-green-50 border border-green-100' : 'bg-green-500/5 border border-green-500/15'}`} data-id="element-1432">
              <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${isLight ? 'text-green-600' : 'text-green-400'}`} data-id="element-1433" />
              <div data-id="element-1434">
                <p className={`text-sm font-semibold ${isLight ? 'text-green-700' : 'text-green-400'}`} data-id="element-1435">
                  30 minutes unlocked!
                </p>
                <p className={`text-xs mt-0.5 ${isLight ? 'text-green-600/80' : 'text-green-400/60'}`} data-id="element-1436">
                  Payment method added — you won't be charged during trial.
                </p>
              </div>
            </div> : <button onClick={() => setShowPaymentModal(true)} className={`w-full rounded-xl p-3.5 mb-5 flex items-start gap-3 text-left transition-all active:scale-[0.98] ${isLight ? 'bg-vexu-50 border border-vexu-100 hover:bg-vexu-100/60' : 'bg-vexu/5 border border-vexu/15 hover:bg-vexu/10'}`} data-id="element-1437">
              <CreditCard className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1438" />
              <div className="flex-1" data-id="element-1439">
                <p className={`text-sm font-semibold ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1440">
                  Double your minutes!
                </p>
                <p className={`text-xs mt-0.5 ${isLight ? 'text-vexu-600/80' : 'text-vexu/60'}`} data-id="element-1441">
                  Add a payment method to get 30 minutes instead of 15. No
                  charge during trial.
                </p>
              </div>
              <ArrowLeft className={`w-4 h-4 mt-1 rotate-180 flex-shrink-0 ${isLight ? 'text-vexu-400' : 'text-vexu/40'}`} data-id="element-1442" />
            </button>}

          <Button variant="secondary" fullWidth onClick={() => onSelectPlan('free')} data-id="element-1443">
            Start Free Trial
          </Button>
        </div>

        {/* Pro Card */}
        <div className={`relative rounded-2xl p-6 transition-all duration-500 ${isLight ? 'bg-white ring-2 ring-vexu/60 shadow-xl shadow-vexu/10 border border-transparent' : 'bg-dark-card/80 backdrop-blur-2xl ring-1 ring-vexu/40 shadow-[0_0_20px_rgba(0,200,83,0.1)] border border-transparent'}`} data-id="element-1444">
          <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md ${isLight ? 'bg-vexu text-white shadow-vexu/20' : 'bg-vexu text-black shadow-vexu/20'}`} data-id="element-1445">
            <Star className="w-3 h-3 fill-current" data-id="element-1446" />
            Recommended
          </div>

          <div className="flex justify-between items-start mb-4" data-id="element-1447">
            <div data-id="element-1448">
              <h3 className={`font-bold text-lg ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1449">
                Pro
              </h3>
              <div className="flex items-baseline gap-1" data-id="element-1450">
                <span className={`text-2xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1451">
                  $29
                </span>
                <span className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-1452">
                  /mo
                </span>
              </div>
            </div>
            <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${isLight ? 'bg-vexu-50 text-vexu-700' : 'bg-vexu/10 text-vexu'}`} data-id="element-1453">
              30-day trial
            </div>
          </div>

          <ul className="space-y-3 mb-6" data-id="element-1454">
            {[{
            icon: Clock,
            text: '2,000 minutes of AI call handling'
          }, {
            icon: Sparkles,
            text: 'Custom AI instructions & behavior'
          }, {
            icon: Globe,
            text: 'Multi-lingual support'
          }, {
            icon: Globe,
            text: 'Automatic language detection'
          }].map((item, i) => <li key={i} className={`flex items-start gap-2.5 text-sm ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-1455">
                <item.icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1456" />
                {item.text}
              </li>)}
          </ul>

          <Button variant="primary" fullWidth onClick={() => onSelectPlan('pro')} data-id="element-1457">
            Start Pro Trial
          </Button>
        </div>
      </div>

      {/* Payment Method Modal */}
      <AnimatePresence data-id="element-1458">
        {showPaymentModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-end justify-center p-4" data-id="element-1459">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !isProcessing && setShowPaymentModal(false)} data-id="element-1460" />
            <motion.div initial={{
          y: 300,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 300,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30
        }} className={`relative w-full max-w-sm rounded-3xl p-6 space-y-5 ${isLight ? 'bg-white shadow-2xl' : 'bg-[#1a1a1a] border border-white/10 shadow-2xl'}`} data-id="element-1461">
              {/* Header */}
              <div className="flex items-start justify-between" data-id="element-1462">
                <div className="flex items-center gap-3" data-id="element-1463">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${isLight ? 'bg-vexu-50' : 'bg-vexu/10'}`} data-id="element-1464">
                    <CreditCard className={`w-5 h-5 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-1465" />
                  </div>
                  <div data-id="element-1466">
                    <h3 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1467">
                      Add Payment Method
                    </h3>
                    <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1468">
                      Unlock 30 minutes — no charge now
                    </p>
                  </div>
                </div>
                <button onClick={() => !isProcessing && setShowPaymentModal(false)} className={`p-1.5 rounded-full ${isLight ? 'text-slate-400 hover:bg-slate-100' : 'text-zinc-500 hover:bg-white/10'}`} data-id="element-1469">
                  <X className="w-5 h-5" data-id="element-1470" />
                </button>
              </div>

              {/* Card Form */}
              <div className="space-y-3" data-id="element-1471">
                <div data-id="element-1472">
                  <label className={`text-xs font-medium mb-1.5 block ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1473">
                    Card Number
                  </label>
                  <input type="text" placeholder="1234 5678 9012 3456" value={cardNumber} onChange={e => setCardNumber(formatCardNumber(e.target.value))} className={`w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-vexu focus:ring-1 focus:ring-vexu/30' : 'bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 focus:border-vexu focus:ring-1 focus:ring-vexu/30'}`} data-id="element-1474" />
                </div>
                <div className="flex gap-3" data-id="element-1475">
                  <div className="flex-1" data-id="element-1476">
                    <label className={`text-xs font-medium mb-1.5 block ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1477">
                      Expiry
                    </label>
                    <input type="text" placeholder="MM/YY" value={expiry} onChange={e => setExpiry(formatExpiry(e.target.value))} className={`w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-vexu focus:ring-1 focus:ring-vexu/30' : 'bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 focus:border-vexu focus:ring-1 focus:ring-vexu/30'}`} data-id="element-1478" />
                  </div>
                  <div className="flex-1" data-id="element-1479">
                    <label className={`text-xs font-medium mb-1.5 block ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-1480">
                      CVC
                    </label>
                    <input type="text" placeholder="123" value={cvc} onChange={e => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))} className={`w-full px-4 py-3 rounded-xl text-sm outline-none transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-vexu focus:ring-1 focus:ring-vexu/30' : 'bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 focus:border-vexu focus:ring-1 focus:ring-vexu/30'}`} data-id="element-1481" />
                  </div>
                </div>
              </div>

              {/* Security note */}
              <div className={`flex items-center gap-2 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1482">
                <Lock className="w-3.5 h-3.5" data-id="element-1483" />
                <span className="text-[11px]" data-id="element-1484">
                  Secured with 256-bit encryption. You won't be charged.
                </span>
              </div>

              {/* Submit */}
              <button onClick={handleAddPayment} disabled={!isCardValid || isProcessing} className={`w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${isCardValid && !isProcessing ? 'bg-vexu text-white hover:bg-vexu-600 active:scale-[0.98]' : isLight ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-white/5 text-zinc-500 cursor-not-allowed'}`} data-id="element-1485">
                <CreditCard className="w-4 h-4" data-id="element-1486" />
                Add Payment Method
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Loading Overlay */}
      <AnimatePresence data-id="element-1487">
        {isProcessing && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-[60] flex flex-col items-center justify-center p-8" data-id="element-1488">
            <div className={`absolute inset-0 ${isLight ? 'bg-white/95' : 'bg-[#0A0A0A]/95'} backdrop-blur-xl`} data-id="element-1489" />
            <div className="relative z-10 flex flex-col items-center text-center w-full max-w-[280px]" data-id="element-1490">
              <div className="relative w-32 h-32 mb-8" data-id="element-1491">
                <motion.div animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.1, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute inset-0 rounded-full border-2 border-vexu/30" data-id="element-1492" />
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute inset-4 rounded-full border-2 border-transparent border-t-vexu border-r-vexu/30" data-id="element-1493" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1494">
                  <motion.div animate={{
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-1495">
                    <CreditCard className="w-10 h-10 text-vexu" data-id="element-1496" />
                  </motion.div>
                </div>
              </div>
              <p className={`text-base font-semibold mb-1 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1497">
                Verifying Payment
              </p>
              <p className={`text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1498">
                Securely processing your card...
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}